import { useEffect, useMemo, useState } from "react";
import { Package, Wrench, NotePencil } from "@phosphor-icons/react";
import { api } from "./api";
import { CapabilityBadges } from "../components/catalogue/CapabilityBadges";

const OTHERS_VALUE = "__others__";

/**
 * Catalogue-aware product/service picker. Replaces a raw text input.
 *
 * Behaviour:
 * - Loads /billing/catalogue/list once
 * - User must pick a catalogue item OR "Others (specify)"
 * - Picking a catalogue item sets the value to that item's name
 * - Picking "Others" reveals a free-text input
 * - In edit mode (initialName matches a catalogue item) the picker selects it;
 *   if it doesn't match any item, the "Others" branch is auto-selected and pre-filled.
 *
 * Iter 38: when a catalogue item is picked, the picker now ALSO emits the
 * full item context via the optional second arg of ``onChange(name, item)``.
 * Consumers can use ``item.hsn_sac_code`` etc. to auto-fill sibling fields.
 * Backward compatible — existing consumers that only consume the first arg
 * continue to work unchanged.
 *
 * Props:
 *  - value         : current product name string
 *  - onChange      : (newName, item?) => void   // item: full catalogue item (null for "Others")
 *  - testidPrefix  : data-testid prefix (e.g. "prop-li", "po-li")
 *  - kindFilter    : optional "product" | "service" filter
 *  - required      : boolean
 */
export default function CatalogueProductPicker({
    value = "",
    onChange,
    testidPrefix = "li",
    kindFilter,
    required = false,
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pickerValue, setPickerValue] = useState("");  // catalogue id OR OTHERS_VALUE OR ""

    useEffect(() => {
        (async () => {
            try {
                const url = kindFilter
                    ? `/billing/catalogue/list?kind=${kindFilter}`
                    : "/billing/catalogue/list";
                const { data } = await api.get(url);
                setItems(data.items || []);
            } catch {
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [kindFilter]);

    // Sync picker selection from `value` (only when items load or value changes externally).
    useEffect(() => {
        if (loading) return;
        if (!value) {
            setPickerValue("");
            return;
        }
        const match = items.find((it) => it.name === value);
        setPickerValue(match ? match.id : OTHERS_VALUE);
    }, [loading, items, value]);

    const grouped = useMemo(() => {
        const products = items.filter((i) => i.kind === "product");
        const services = items.filter((i) => i.kind === "service");
        return { products, services };
    }, [items]);

    const handlePicker = (next) => {
        setPickerValue(next);
        if (next === "" || next === OTHERS_VALUE) {
            // Clear value so user types fresh in the Others input
            onChange("", null);
        } else {
            const item = items.find((it) => it.id === next);
            // Iter 38: emit the full catalogue item so consumers can auto-fill
            // siblings (HSN/SAC, GST defaults, etc.) without re-querying.
            if (item) onChange(item.name, item);
        }
    };

    const isOthers = pickerValue === OTHERS_VALUE;

    return (
        <div className="space-y-2" data-testid={`${testidPrefix}-product-picker`}>
            <select
                value={pickerValue}
                onChange={(e) => handlePicker(e.target.value)}
                required={required}
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                data-testid={`${testidPrefix}-product-select`}
            >
                <option value="">— Choose a product / service —</option>
                {grouped.products.length > 0 && (
                    <optgroup label="Products">
                        {grouped.products.map((it) => (
                            <option key={it.id} value={it.id}>{it.name}</option>
                        ))}
                    </optgroup>
                )}
                {grouped.services.length > 0 && (
                    <optgroup label="Services">
                        {grouped.services.map((it) => (
                            <option key={it.id} value={it.id}>{it.name}</option>
                        ))}
                    </optgroup>
                )}
                <option value={OTHERS_VALUE}>── Others (specify name below) ──</option>
            </select>

            {isOthers && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
                    <NotePencil size={14} className="text-amber-700 shrink-0" weight="fill" />
                    <input
                        type="text"
                        required={required}
                        value={value}
                        onChange={(e) => onChange(e.target.value, null)}
                        placeholder="Type custom product / service name"
                        className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-amber-700/50"
                        data-testid={`${testidPrefix}-product-other-input`}
                    />
                </div>
            )}

            {!loading && pickerValue && pickerValue !== OTHERS_VALUE && (() => {
                const item = items.find((it) => it.id === pickerValue);
                if (!item) return null;
                const Icon = item.kind === "product" ? Package : Wrench;
                return (
                    <div className="space-y-1">
                        <div className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                            <Icon size={10} weight="fill" /> Catalogue · {item.kind}
                        </div>
                        {/* Iter C.2.1.CAP — Capability preview in product picker. */}
                        {(item.capabilities || []).length > 0 && (
                            <CapabilityBadges capabilities={item.capabilities} size="sm" />
                        )}
                    </div>
                );
            })()}
        </div>
    );
}
